import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { createSupplier, updateSupplier, fetchSupplierInfo } from '../../services/supplierService';
import { Supplier, SupplierFormData, Contact, PerplexitySupplierInfo } from '../../types/supplier';

interface SupplierFormProps {
  supplier?: Supplier;
  onSubmit?: () => void;
  onCancel?: () => void;
}

const initialFormData: SupplierFormData = {
  name: '',
  type: 'garage',
  address: '',
  contactName: '',
  contactPhone: '',
  email: '',
  services: [],
  notes: '',
  websiteUrl: '',
  hours: {},
  additionalContacts: [],
};

const SupplierForm: React.FC<SupplierFormProps> = ({ supplier, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const [formData, setFormData] = useState<SupplierFormData>(supplier || initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchLocation, setSearchLocation] = useState('');
  const [perplexityData, setPerplexityData] = useState<PerplexitySupplierInfo | null>(null);
  const [showPerplexityPreview, setShowPerplexityPreview] = useState(false);
  const [newContact, setNewContact] = useState<Contact>({
    name: '',
    phone: '',
    email: '',
    role: '',
  });
  const [showContactDialog, setShowContactDialog] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchPerplexity = async () => {
    if (!formData.name || !searchLocation) {
      setError(t('suppliers.perplexity.missingFields'));
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const info = await fetchSupplierInfo(formData.name, searchLocation);
      if (!info) {
        setError(t('suppliers.perplexity.notFound'));
        return;
      }
      setPerplexityData(info);
      setShowPerplexityPreview(true);
    } catch (error) {
      console.error('Error fetching supplier info:', error);
      if (error instanceof Error) {
        if (error.message.includes('configuration')) {
          setError(t('suppliers.perplexity.configError'));
        } else if (error.message.includes('network')) {
          setError(t('suppliers.perplexity.networkError'));
        } else {
          setError(t('suppliers.perplexity.apiError'));
        }
      } else {
        setError(t('suppliers.perplexity.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const applyPerplexityData = () => {
    if (!perplexityData) return;

    setFormData((prev) => ({
      ...prev,
      name: perplexityData.name || prev.name,
      address: perplexityData.address || prev.address,
      contactPhone: perplexityData.phone || prev.contactPhone,
      email: perplexityData.email || prev.email,
      websiteUrl: perplexityData.website || prev.websiteUrl,
      hours: perplexityData.hours || prev.hours,
      notes: prev.notes + (prev.notes ? '\n\n' : '') + 
        (perplexityData.rating 
          ? `Rating: ${perplexityData.rating}/5${perplexityData.reviews ? ` (${perplexityData.reviews} reviews)` : ''}\n` 
          : '') +
        (perplexityData.socialMedia 
          ? `Social Media:\n${Object.entries(perplexityData.socialMedia)
              .filter(([_, url]) => url)
              .map(([platform, url]) => `${platform}: ${url}`)
              .join('\n')}` 
          : '')
    }));
    setShowPerplexityPreview(false);
  };

  const handleAddContact = () => {
    if (newContact.name && newContact.phone) {
      setFormData((prev) => ({
        ...prev,
        additionalContacts: [...(prev.additionalContacts || []), newContact],
      }));
      setNewContact({ name: '', phone: '', email: '', role: '' });
      setShowContactDialog(false);
    }
  };

  const handleRemoveContact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      additionalContacts: prev.additionalContacts?.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization?.id) return;

    setLoading(true);
    setError(null);
    try {
      if (supplier?.id) {
        await updateSupplier(supplier.id, formData);
      } else {
        await createSupplier(currentOrganization.id, formData);
      }
      onSubmit?.();
    } catch (error) {
      console.error('Error saving supplier:', error);
      setError(t('suppliers.messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {supplier ? t('suppliers.edit') : t('suppliers.add')}
            </Typography>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              name="name"
              label={t('suppliers.form.name')}
              value={formData.name}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              select
              name="type"
              label={t('suppliers.form.type')}
              value={formData.type}
              onChange={handleInputChange}
            >
              <MenuItem value="garage">{t('suppliers.type.garage')}</MenuItem>
              <MenuItem value="dealer">{t('suppliers.type.dealer')}</MenuItem>
              <MenuItem value="parts">{t('suppliers.type.parts')}</MenuItem>
              <MenuItem value="other">{t('suppliers.type.other')}</MenuItem>
            </TextField>
          </Grid>

          {/* Perplexity Search */}
          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label={t('suppliers.form.location')}
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleSearchPerplexity}
                disabled={loading || !formData.name || !searchLocation}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                color={perplexityData ? "success" : "primary"}
              >
                {loading 
                  ? t('suppliers.perplexity.searching')
                  : perplexityData 
                    ? t('suppliers.perplexity.search')
                    : t('suppliers.perplexity.search')
                }
              </Button>
            </Box>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              name="contactName"
              label={t('suppliers.form.contactName')}
              value={formData.contactName}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              name="contactPhone"
              label={t('suppliers.form.contactPhone')}
              value={formData.contactPhone}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="email"
              type="email"
              label={t('suppliers.form.email')}
              value={formData.email}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              name="address"
              label={t('suppliers.form.address')}
              value={formData.address}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="websiteUrl"
              label={t('suppliers.form.website')}
              value={formData.websiteUrl}
              onChange={handleInputChange}
              type="url"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="notes"
              label={t('suppliers.form.notes')}
              value={formData.notes}
              onChange={handleInputChange}
              helperText={t('suppliers.form.notesHelper')}
            />
          </Grid>

          {/* Additional Contacts */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">
                {t('suppliers.form.additionalContacts')}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setShowContactDialog(true)}
              >
                {t('common.add')}
              </Button>
            </Box>
            <List>
              {formData.additionalContacts?.map((contact, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={contact.name}
                    secondary={`${contact.role} - ${contact.phone}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveContact(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : t('common.save')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Perplexity Preview Dialog */}
      <Dialog
        open={showPerplexityPreview}
        onClose={() => setShowPerplexityPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('suppliers.perplexity.preview')}</DialogTitle>
        <DialogContent>
          {perplexityData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('suppliers.perplexity.foundInfo')}:
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Typography>
                  <strong>{t('suppliers.form.name')}:</strong> {perplexityData.name}
                </Typography>
                
                <Typography>
                  <strong>{t('suppliers.form.address')}:</strong> {perplexityData.address}
                </Typography>
                
                <Typography>
                  <strong>{t('suppliers.form.phone')}:</strong> {perplexityData.phone}
                </Typography>
                
                {perplexityData.email && (
                  <Typography>
                    <strong>{t('suppliers.form.email')}:</strong> {perplexityData.email}
                  </Typography>
                )}
                
                {perplexityData.website && (
                  <Typography>
                    <strong>{t('suppliers.form.website')}:</strong> {perplexityData.website}
                  </Typography>
                )}

                {perplexityData.hours && Object.keys(perplexityData.hours).length > 0 && (
                  <Box>
                    <Typography><strong>{t('suppliers.form.hours')}:</strong></Typography>
                    {Object.entries(perplexityData.hours).map(([day, hours]) => (
                      <Typography key={day} sx={{ pl: 2 }}>
                        {day}: {hours.open} - {hours.close}
                      </Typography>
                    ))}
                  </Box>
                )}

                {perplexityData.socialMedia && (
                  <Box>
                    <Typography><strong>{t('suppliers.form.socialMedia')}:</strong></Typography>
                    {perplexityData.socialMedia.facebook && (
                      <Typography sx={{ pl: 2 }}>Facebook: {perplexityData.socialMedia.facebook}</Typography>
                    )}
                    {perplexityData.socialMedia.linkedin && (
                      <Typography sx={{ pl: 2 }}>LinkedIn: {perplexityData.socialMedia.linkedin}</Typography>
                    )}
                    {perplexityData.socialMedia.twitter && (
                      <Typography sx={{ pl: 2 }}>Twitter: {perplexityData.socialMedia.twitter}</Typography>
                    )}
                  </Box>
                )}

                {perplexityData.rating && (
                  <Typography>
                    <strong>{t('suppliers.rating')}:</strong> {perplexityData.rating}/5 
                    {perplexityData.reviews && ` (${perplexityData.reviews} ${t('suppliers.reviews')})`}
                  </Typography>
                )}

                {perplexityData.lastVerified && (
                  <Typography variant="caption" color="text.secondary">
                    {t('suppliers.perplexity.lastVerified')}: {new Date(perplexityData.lastVerified).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPerplexityPreview(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={applyPerplexityData} variant="contained" color="primary">
            {t('suppliers.perplexity.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog
        open={showContactDialog}
        onClose={() => setShowContactDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('suppliers.form.addContact')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('suppliers.form.contactName')}
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('suppliers.form.contactPhone')}
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label={t('suppliers.form.email')}
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('suppliers.form.role')}
                value={newContact.role}
                onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddContact}
            variant="contained"
            color="primary"
            disabled={!newContact.name || !newContact.phone}
          >
            {t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierForm;
